import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import ErrorBoundary from "./components/ErrorBoundary";
import Layout from "./layouts/Layout";
import Home from "./pages/Home";
import About from "./pages/About";
import Blog from "./pages/Blog";
import Privacy from "./pages/Privacy";
import Kvkk from "./pages/Kvkk";
import Cookies from "./pages/Cookies";
import NotFound from "./pages/NotFound";
import GoogleReklamlariYonetimi from "./pages/GoogleReklamlariYonetimi";
import TesisatciWebSitesi from "./pages/sector/TesisatciWebSitesi";

// Lazy load BlogPost for better initial bundle size
const BlogPost = lazy(() => import("./pages/BlogPost"));

// Loading skeleton for blog posts
const BlogLoadingSkeleton = () => (
  <div className="min-h-screen bg-gray-50">
    <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8 space-y-6">
      <Skeleton className="h-12 w-32" />
      <Skeleton className="h-16 w-3/4" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-96 w-full" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-6 w-5/6" />
    </div>
  </div>
);

const queryClient = new QueryClient();

const App = () => {
  // Global error handler
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      // Analytics ve extension hatalarını görmezden gel
      if (event.message && (
        event.message.includes('contentOverview') ||
        event.message.includes('chrome-extension')
      )) {
        event.preventDefault();
        return;
      }
      console.error('Global error:', event.error);
    };
    
    const handleRejection = (event: PromiseRejectionEvent) => {
      // 401 hatalarını ve analytics hatalarını görmezden gel (uygulamayı durdurmayalım)
      if (event.reason && (
        String(event.reason).includes('401') || 
        String(event.reason).includes('contentOverview') ||
        String(event.reason).includes('Request failed with status code 401')
      )) {
        console.warn('⚠️ Third-party service error (non-critical):', event.reason);
        event.preventDefault();
        return;
      }
      console.error('Unhandled promise rejection:', event.reason);
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);
    
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              {/* Standalone sector landing pages — no shared header/footer */}
              <Route path="/tesisatci-web-sitesi" element={<TesisatciWebSitesi />} />
              {/* Main site — wrapped in shared Layout */}
              <Route
                path="*"
                element={
                  <Layout>
                    <Routes>
                      <Route path="/" element={<Home />} />
                      <Route path="/hakkimda" element={<About />} />
                      <Route path="/blog" element={<Blog />} />
                      <Route
                        path="/blog/:slug"
                        element={
                          <Suspense fallback={<BlogLoadingSkeleton />}>
                            <BlogPost />
                          </Suspense>
                        }
                      />
                      <Route path="/gizlilik-politikasi" element={<Privacy />} />
                      <Route path="/kvkk" element={<Kvkk />} />
                      <Route path="/cerez-politikasi" element={<Cookies />} />
                      <Route path="/google-reklamlari-yonetimi" element={<GoogleReklamlariYonetimi />} />
                      <Route path="*" element={<NotFound />} />
                    </Routes>
                  </Layout>
                }
              />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
